import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { DateTimePicker } from '@mui/x-date-pickers'
import { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { AppDialog } from '../../../components'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { showAlertAction } from '../../../store/alerts'
import { fetchItemCategoryAction } from '../../../store/itemCategory'
import {
  AddItemStockType,
  getItemsV3,
  ItemV3,
  createStockInvoice,
  getNextStockInvoiceNo,
} from '../../../services/items/items'
import { Vendor } from '../../../services'
import { fetchVendorsAction } from '../../../store/vendors'
import { formatPartyAutocompleteLabel, getFormattedDate } from '../../../utils'

export type PreviewLine = {
  itemId: number
  itemName: string
  quantity: number
}

const NOTICE_ERROR_CODE = 1103

const STOCK_TYPES: { value: AddItemStockType; label: string }[] = [
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'SCRAP', label: 'Scrap' },
]

export interface AddStockDialogProps {
  onClose: () => void
  onSuccess?: () => void
  /** Optional item to preselect when opening the dialog (e.g. from Item History page) */
  initialItemId?: number
  /** Optional vendor to preselect when opening the dialog */
  initialVendor?: Vendor | null
  /** Called when user chooses \"Add Vendor\" from the vendor dropdown */
  onRequestAddVendor?: () => void
}

export const AddStockDialog: FC<AddStockDialogProps> = ({
  onClose,
  onSuccess,
  initialItemId,
  initialVendor,
  onRequestAddVendor,
}) => {
  const dispatchAction = useAppDispatch()
  const {
    list: { data: vendorsData },
  } = useAppSelector((state) => state.manageVendors)

  const [itemsList, setItemsList] = useState<ItemV3[]>([])
  const [itemOptions, setItemOptions] = useState<
    Array<{ id: number; label: string }>
  >([])
  const [previewLines, setPreviewLines] = useState<PreviewLine[]>([])
  const [itemId, setItemId] = useState<number | ''>('')
  const [selectedItem, setSelectedItem] = useState<{
    id: number
    label: string
  } | null>(null)
  const [type, setType] = useState<AddItemStockType>('PURCHASE')
  const [quantity, setQuantity] = useState<string>('')
  const [quantityError, setQuantityError] = useState<string>('')
  const [transactionDate, setTransactionDate] = useState<Date | null>(() => new Date())
  const [reference, setReference] = useState<string>('')
  const [invoiceNo, setInvoiceNo] = useState<string>('')
  const [invoiceValue, setInvoiceValue] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [previewError, setPreviewError] = useState<string>('')
  const [disableEnter, setDisableEnter] = useState(false)
  const quantityInputRef = useRef<HTMLInputElement | null>(null)

  const availableItemOptions = useMemo(
    () =>
      itemOptions.filter(
        (opt) => !previewLines.some((l) => l.itemId === opt.id)
      ),
    [itemOptions, previewLines]
  )

  const ADD_NEW_VENDOR = 'Add Vendor'
  const [vendorOptions, setVendorOptions] = useState<
    Array<{ id: number; label: string }>
  >([])
  const [selectedVendor, setSelectedVendor] = useState<{
    id: number
    label: string
  } | null>(null)

  useEffect(() => {
    getNextStockInvoiceNo()
      .then((res) => {
        if (res.success && typeof res.data === 'string' && res.data.trim()) {
          setInvoiceNo(res.data.trim())
        }
      })
      .catch(() => {
        /* optional prefill — leave field empty on failure */
      })
  }, [])

  useEffect(() => {
    dispatchAction(fetchVendorsAction())
    getItemsV3()
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setItemsList(res.data.data)
          const mappedItems = res.data.data.map((item) => ({
            id: item.id,
            label: item.itemName,
          }))
          setItemOptions(mappedItems)
        }
      })
      .catch(() => {
        setItemsList([])
        setItemOptions([])
      })
  }, [dispatchAction])

  useEffect(() => {
    const addVendorOption = { id: 0, label: ADD_NEW_VENDOR }
    if (Array.isArray(vendorsData) && vendorsData.length > 0) {
      const mapped = vendorsData.map((vendor) => ({
        id: vendor.id,
        label: formatPartyAutocompleteLabel(
          vendor.name,
          vendor.mobile,
          vendor.address
        ),
      }))
      setVendorOptions([addVendorOption, ...mapped])
    } else {
      setVendorOptions([addVendorOption])
    }
  }, [vendorsData])

  useEffect(() => {
    if (initialItemId != null && initialItemId > 0 && itemsList.length > 0) {
      const found = itemsList.find((i) => i.id === initialItemId)
      if (found) {
        setItemId(initialItemId)
        setSelectedItem({
          id: found.id,
          label: found.itemName,
        })
      }
    }
  }, [initialItemId, itemsList])

  useEffect(() => {
    if (initialVendor) {
      const option = {
        id: initialVendor.id,
        label: formatPartyAutocompleteLabel(
          initialVendor.name,
          initialVendor.mobile,
          initialVendor.address
        ),
      }
      setSelectedVendor(option)
    }
  }, [initialVendor])

  const validateAddLine = useCallback((): boolean => {
    if (itemId === '' || itemId <= 0) {
      setQuantityError('Please select an item')
      return false
    }
    const q = Number(quantity)
    if (!quantity.trim()) {
      setQuantityError('Quantity is required')
      return false
    }
    if (Number.isNaN(q) || q <= 0) {
      setQuantityError('Quantity must be greater than zero')
      return false
    }
    setQuantityError('')
    return true
  }, [itemId, quantity])

  const handleAddLine = useCallback(() => {
    if (!validateAddLine() || selectedItem == null) return
    const q = Number(quantity)
    setPreviewError('')
    setPreviewLines((prev) => [
      ...prev,
      {
        itemId: selectedItem.id,
        itemName: selectedItem.label,
        quantity: q,
      },
    ])
    setSelectedItem(null)
    setItemId('')
    setQuantity('')
    setQuantityError('')
  }, [validateAddLine, selectedItem, quantity])

  const shouldSubmitFromLineInputs = useCallback(() => {
    return !selectedItem && !quantity.trim()
  }, [selectedItem, quantity])

  const handleRemoveLine = useCallback((index: number) => {
    setPreviewLines((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleVendorChange = useCallback(
    (_event: any, newValue: { id: number; label: string } | null) => {
      if (!newValue) {
        setSelectedVendor(null)
        return
      }
      if (newValue.label === ADD_NEW_VENDOR) {
        setSelectedVendor(null)
        onRequestAddVendor?.()
        return
      }
      setSelectedVendor(newValue)
    },
    [onRequestAddVendor]
  )

  const handleSubmit = useCallback(async () => {
    if (submitting) return
    if (previewLines.length < 1) {
      setPreviewError('Add at least one item')
      return
    }
    if (!selectedVendor || selectedVendor.id <= 0) {
      dispatchAction(
        showAlertAction({
          message: 'Please select a vendor',
          severity: 'error',
        })
      )
      return
    }
    if (!transactionDate) {
      dispatchAction(
        showAlertAction({
          message: 'Please select a transaction date',
          severity: 'error',
        })
      )
      return
    }
    setPreviewError('')
    setSubmitting(true)
    try {
      const payload = {
        transactionType: type,
        vendorId: selectedVendor.id,
        transactionDate: getFormattedDate(transactionDate),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(invoiceNo.trim() ? { invoiceNo: invoiceNo.trim() } : {}),
        ...(invoiceValue.trim() && !Number.isNaN(Number(invoiceValue))
          ? { invoiceValue: Number(invoiceValue) }
          : {}),
        items: previewLines.map((l) => ({
          itemId: l.itemId,
          quantity: l.quantity,
        })),
      }
      const result = await createStockInvoice(payload)
      if (result.success) {
        dispatchAction(
          showAlertAction({
            message: result.message || 'Stock added successfully',
            severity: 'success',
          })
        )
        dispatchAction(fetchItemCategoryAction())
        onSuccess?.()
        onClose()
      } else {
        const resultCode = Number((result as any)?.code)
        if (resultCode === NOTICE_ERROR_CODE) return
        dispatchAction(
          showAlertAction({
            message: result.message || 'Something went wrong',
            severity: 'error',
          })
        )
      }
    } catch (err: any) {
      const errorCode = Number(err?.response?.data?.code)
      if (errorCode === NOTICE_ERROR_CODE) return
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong, please contact us'
      dispatchAction(
        showAlertAction({
          message,
          severity: 'error',
        })
      )
    } finally {
      setSubmitting(false)
    }
  }, [
    type,
    previewLines,
    selectedVendor,
    transactionDate,
    reference,
    submitting,
    dispatchAction,
    onClose,
    onSuccess,
  ])

  return (
    <AppDialog
      id="add-stock-transactions"
      title="Add Stock Quantity"
      submitButtonText="SUBMIT"
      onClose={onClose}
      onSubmit={handleSubmit}
      disableEnter={disableEnter}
      close={false}
      maxWidth="md"
    >
      <Box sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          {/* Common fields row */}
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              size="small"
              options={vendorOptions}
              value={selectedVendor}
              onChange={handleVendorChange}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField {...params} label="Select Vendor" required />
              )}
              renderOption={(props, option) => (
                <Typography
                  {...props}
                  noWrap
                  sx={{
                    color: option.label === ADD_NEW_VENDOR ? 'green' : 'inherit',
                    fontWeight:
                      option.label === ADD_NEW_VENDOR ? 600 : 'inherit',
                  }}
                >
                  {option.label}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <FormControl fullWidth size="small" required>
              <InputLabel id="add-stock-type-label">Type</InputLabel>
              <Select
                labelId="add-stock-type-label"
                value={type}
                label="Type"
                onChange={(e) =>
                  setType(e.target.value as AddItemStockType)
                }
              >
                {STOCK_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <DateTimePicker
              label="Transaction date & time"
              value={transactionDate}
              onChange={(date) => setTransactionDate(date)}
              format="dd/MM/yyyy hh:mm a"
              ampmInClock={false}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
         
         <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Invoice No (optional)"
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Invoice Value (optional)"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={invoiceValue}
              onChange={(e) => setInvoiceValue(e.target.value)}
            />
          </Grid>
           <Grid item xs={12} md={6} lg={4}>
            <TextField
              fullWidth
              size="small"
              label="Reference/Note"
              placeholder="Reference/Note"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </Grid>
          {/* Add-item row: Select Item | Quantity | ADD */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="top">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  
                  size="small"
                  options={availableItemOptions}
                  value={selectedItem}
                  onChange={(_event, newValue) => {
                    if (!newValue) {
                      setSelectedItem(null)
                      setItemId('')
                    } else {
                      setSelectedItem(newValue)
                      setItemId(newValue.id)
                      requestAnimationFrame(() => {
                        quantityInputRef.current?.focus()
                      })
                    }
                    if (quantityError) setQuantityError('')
                  }}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Item"
                      required
                      onFocus={() => setDisableEnter(true)}
                      onBlur={() => setDisableEnter(false)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return
                        if (shouldSubmitFromLineInputs()) {
                          setDisableEnter(false)
                          return
                        }
                        e.preventDefault()
                        e.stopPropagation()
                        handleAddLine()
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Quantity"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value)
                    if (quantityError) setQuantityError('')
                  }}
                  onFocus={() => setDisableEnter(true)}
                  onBlur={() => setDisableEnter(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (shouldSubmitFromLineInputs()) {
                        setDisableEnter(false)
                        return
                      }
                      e.preventDefault()
                      e.stopPropagation()
                      handleAddLine()
                    }
                  }}
                  inputRef={quantityInputRef}
                  error={Boolean(quantityError)}
                  helperText={quantityError}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddLine}
                  aria-label="Add item to list"
                >
                  ADD
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {/* Preview list */}
          <Grid item xs={12}>
            {previewError && (
              <Typography color="error" variant="body2" sx={{ mb: 0 }}>
                {previewError}
              </Typography>
            )}
           
            <List
              dense
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 220,
                overflow: 'auto',
              }}
            >
              {previewLines.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No items added yet. Select an item, enter quantity, then click ADD."
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              ) : (
                previewLines.map((line, index) => (
                  <ListItem
                    key={`${line.itemId}-${index}`}
                    divider={index < previewLines.length - 1}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant="body2">{line.itemName}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <Typography variant="body2">Qty: {line.quantity}</Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={2}
                        sx={{ textAlign: { xs: 'left', md: 'right' } }}
                      >
                        <IconButton
                          aria-label={`Remove ${line.itemName} from list`}
                          onClick={() => handleRemoveLine(index)}
                          size="small"
                          sx={{ color: 'crimson' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))
              )}
            </List>
          </Grid>
          {/* Invoice metadata */}
         
        </Grid>
      </Box>
    </AppDialog>
  )
}

export default AddStockDialog
